import React from 'react';

import type { SpacingScale } from '../../utilities/shared/sizes';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { Small } from '../../text/Small';

const CompletionWheelContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '$6',
  whiteSpace: 'nowrap',
});
CompletionWheelContainer.displayName = 'CompletionWheelContainer';

const CompletionWheelTrack = styled('circle', {
  stroke: colors.strokeNeutralLight,
  fill: 'none',
  [darkThemeSelector]: {
    stroke: colors.strokeNeutralDark,
  },
});
CompletionWheelTrack.displayName = 'CompletionWheelTrack';

const CompletionWheelProgress = styled('circle', {
  fill: 'none',
  strokeLinecap: 'round',
  stroke: colors.chartStrokeBrandLight,
  [darkThemeSelector]: {
    stroke: colors.chartStrokeBrandDark,
  },
});
CompletionWheelProgress.displayName = 'CompletionWheelProgress';

export interface CompletionWheelProps {
  /**
   * Fraction completed, from 0 to 1.
   */
  value: number;
  /**
   * Optional label displayed next to the wheel (e.g. "2/5").
   */
  label?: string;
  /**
   * Diameter of the wheel in pixels. Must be a valid spacing scale value.
   * @default 16
   */
  size?: SpacingScale;
  /**
   * Stroke width of the wheel in pixels.
   * @default 2
   */
  strokeWidth?: number;
}

export const CompletionWheel = React.forwardRef<HTMLDivElement, CompletionWheelProps>(
  ({ value, label, size = 16, strokeWidth = 2, ...rest }, ref) => {
    const clamped = Math.min(1, Math.max(0, value));
    const clampedStrokeWidth = Math.min(Math.max(1, strokeWidth), size - 1);
    const radius = (size - clampedStrokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - clamped);
    const center = size / 2;

    return (
      <CompletionWheelContainer ref={ref} {...rest}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          fill="none"
          style={{ flexShrink: 0 }}
        >
          <CompletionWheelTrack
            cx={center}
            cy={center}
            r={radius}
            style={{ strokeWidth: clampedStrokeWidth }}
          />
          {clamped > 0 && (
            <CompletionWheelProgress
              cx={center}
              cy={center}
              r={radius}
              style={{
                strokeWidth: clampedStrokeWidth,
                strokeDasharray: circumference,
                strokeDashoffset: offset,
              }}
              transform={`rotate(-90 ${center} ${center})`}
            />
          )}
        </svg>
        {label && <Small>{label}</Small>}
      </CompletionWheelContainer>
    );
  },
);
